import { APIClient } from '@/helpers/api_helper';

import type {
  AssetItem as AssetPoolItem,
  AssetPool,
  AssetPoolFilters,
  ChangeActivityStatusPayload,
  CreateActivityFromUnplannedTaskPayload,
  CrewPool,
  CrewPoolFilters,
  ExecutionCapturePayload,
  PlannerActivity,
  PlannerActivityCreatePayload,
  PlannerActivityDetail,
  PlannerActivityListFilters,
  PlannerActivityTimePayload,
  PlannerActivityUpdatePayload,
  PlannerAssetAssignmentsPayload,
  PlannerCrewAssignmentsPayload,
  PlannerExecutionLine,
  PlannerReorderPayload,
  PlannerSingleAssetAssignmentPayload,
  PlannerSingleCrewAssignmentPayload,
  SchedulingGridFilters,
  UnplannedWorkFilters,
  UnplannedWorkItem,
} from '../types';

/**
 * Planner API client — wraps the shared axios `APIClient`.
 *
 * Endpoints mirrored from legacy `src/pages/Planner/services/plannerService.ts`
 * and the route table in `src/helpers/url_helper.ts`:
 *
 *   GET    /Planner/cards?from&to              — scheduling grid window
 *   GET    /Planner/{id}                       — single activity detail
 *   GET    /Planner/unplanned-work             — queue of unscheduled tasks
 *   GET    /Planner/crew-load?date             — crew availability + assigned
 *   GET    /Planner/asset-load?date            — asset availability + assigned
 *   GET    /Planner/site-workload?date         — Agent C (site rollup)
 *   POST   /Planner                            — create activity
 *   POST   /Planner/from-unplanned-task        — schedule unplanned work
 *   PUT    /Planner/{id}                       — full update
 *   PATCH  /Planner/{id}/time                  — adjust schedule window
 *   PATCH  /Planner/{id}/crew                  — replace crew set
 *   PATCH  /Planner/{id}/assets                — replace asset set
 *   PATCH  /Planner/{id}/crew-member           — add a single crew (non-destructive)
 *   PATCH  /Planner/{id}/asset                 — add a single asset (non-destructive)
 *   PATCH  /Planner/{id}/status                — change activity status
 *   PATCH  /Planner/reorder                    — reorder within a day
 *   DELETE /Planner/{id}                       — soft-delete activity
 *   GET    /Planner/{id}/execution-lines       — execution lines for an activity
 *   POST   /Planner/{id}/execution-capture     — capture execution results
 *
 * Agent A owns this file. Agents B and C append below the marked seams.
 */

const BASE = '/Planner';

const api = new APIClient();

/* -------------------------------------------------------------------------- */
/* Response normalisation                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Normalise an API response that may be wrapped in `{ items: [] }` (or a few
 * other legacy shapes). Mirrors the helper in the legacy planner service.
 */
function toArray<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    const arr = obj.items ?? obj.result ?? obj.data ?? obj.cards;
    if (Array.isArray(arr)) return arr as T[];
  }
  return [];
}

/**
 * Extract `result` from `CreateUpdateResponse<T>` wrappers when present.
 */
function unwrapResult<T>(value: unknown): T {
  if (
    value &&
    typeof value === 'object' &&
    'result' in (value as Record<string, unknown>)
  ) {
    return (value as { result: T }).result;
  }
  return value as T;
}

/* -------------------------------------------------------------------------- */
/* Scheduling grid + activities                                                */
/* -------------------------------------------------------------------------- */

export const plannerApi = {
  /**
   * `GET /Planner/cards?from&to` — fetches scheduled activities within the
   * date range. Used by the scheduling grid (both crew lanes + asset lanes).
   */
  listActivities: async (
    filters: PlannerActivityListFilters,
    signal?: AbortSignal,
  ): Promise<PlannerActivity[]> => {
    const response = await api.get<unknown>(
      `${BASE}/cards`,
      { from: filters.from, to: filters.to },
      signal ? { signal } : undefined,
    );
    return toArray<PlannerActivity>(response);
  },

  /**
   * Scheduling-grid view — alias for `listActivities` that respects the wider
   * `SchedulingGridFilters` shape (mode / supervisor scope are applied
   * client-side after the fetch).
   */
  getSchedulingGrid: async (
    filters: SchedulingGridFilters,
    signal?: AbortSignal,
  ): Promise<PlannerActivity[]> => {
    const response = await api.get<unknown>(
      `${BASE}/cards`,
      { from: filters.from, to: filters.to },
      signal ? { signal } : undefined,
    );
    return toArray<PlannerActivity>(response);
  },

  /**
   * `GET /Planner/{id}` — the activity detail endpoint. Returns the lean card
   * shape PLUS the `crewAssignments` + `assetAssignments` arrays the drawer
   * uses to render the per-row remove buttons.
   */
  getActivity: async (id: string): Promise<PlannerActivityDetail> => {
    const response = await api.get<PlannerActivityDetail>(`${BASE}/${id}`);
    return response;
  },

  createActivity: (
    payload: PlannerActivityCreatePayload,
  ): Promise<PlannerActivity> => {
    return api.create<PlannerActivity>(BASE, payload);
  },

  /**
   * `POST /Planner/from-unplanned-task` — schedule an unplanned task by
   * promoting it into the planner grid.
   */
  scheduleUnplannedTask: (
    payload: CreateActivityFromUnplannedTaskPayload,
  ): Promise<PlannerActivity> => {
    return api.create<PlannerActivity>(`${BASE}/from-unplanned-task`, payload);
  },

  updateActivity: (
    id: string,
    payload: PlannerActivityUpdatePayload,
  ): Promise<PlannerActivity> => {
    return api.put<PlannerActivity>(`${BASE}/${id}`, payload);
  },

  /**
   * `PATCH /Planner/{id}/time` — adjust the activity's schedule window.
   * Fires on drag-to-new-date in the grid.
   */
  updateActivityTime: (
    id: string,
    payload: PlannerActivityTimePayload,
  ): Promise<PlannerActivity> => {
    return api.update<PlannerActivity>(`${BASE}/${id}/time`, payload);
  },

  updateCrewAssignments: (
    id: string,
    payload: PlannerCrewAssignmentsPayload,
  ): Promise<PlannerActivity> => {
    return api.update<PlannerActivity>(`${BASE}/${id}/crew`, payload);
  },

  updateAssetAssignments: (
    id: string,
    payload: PlannerAssetAssignmentsPayload,
  ): Promise<PlannerActivity> => {
    return api.update<PlannerActivity>(`${BASE}/${id}/assets`, payload);
  },

  /**
   * Non-destructive single crew assignment. Adds without replacing the
   * existing crew set. Returns the activity with the merged crew.
   */
  addCrewMember: async (
    activityId: string,
    payload: PlannerSingleCrewAssignmentPayload,
  ): Promise<PlannerActivity> => {
    const response = await api.update<unknown>(
      `${BASE}/${activityId}/crew-member`,
      payload,
    );
    return unwrapResult<PlannerActivity>(response);
  },

  /**
   * Non-destructive single asset assignment. Adds without replacing existing
   * assets. Used when an asset pool chip is dragged onto an activity card.
   */
  addAsset: async (
    activityId: string,
    payload: PlannerSingleAssetAssignmentPayload,
  ): Promise<PlannerActivity> => {
    const response = await api.update<unknown>(
      `${BASE}/${activityId}/asset`,
      payload,
    );
    return unwrapResult<PlannerActivity>(response);
  },

  changeStatus: async (
    activityId: string,
    payload: ChangeActivityStatusPayload,
  ): Promise<PlannerActivity> => {
    const response = await api.update<unknown>(
      `${BASE}/${activityId}/status`,
      payload,
    );
    return unwrapResult<PlannerActivity>(response);
  },

  reorder: (payload: PlannerReorderPayload): Promise<void> => {
    return api.update<void>(`${BASE}/reorder`, payload);
  },

  deleteActivity: async (activityId: string): Promise<void> => {
    await api.delete(`${BASE}/${activityId}`);
  },

  /**
   * Remove a single crew member from a PlannerActivity.
   * `DELETE /Planner/{activityId}/crew-member/{crewMemberId}` — matches legacy
   * `removeCrewMemberFromActivity` in `pages/Planner/services/plannerService.ts`.
   * Returns the updated activity so the drawer / grid caches can refresh.
   */
  removeCrewMember: async (
    activityId: string,
    crewMemberId: string,
  ): Promise<PlannerActivity> => {
    const response = await api.delete<unknown>(
      `${BASE}/${activityId}/crew-member/${crewMemberId}`,
    );
    return unwrapResult<PlannerActivity>(response);
  },

  /**
   * Remove a single asset from a PlannerActivity.
   * `DELETE /Planner/{activityId}/asset/{assetId}` — matches legacy
   * `removeAssetFromActivity` in `pages/Planner/services/plannerService.ts`.
   */
  removeAsset: async (
    activityId: string,
    assetId: string,
  ): Promise<PlannerActivity> => {
    const response = await api.delete<unknown>(
      `${BASE}/${activityId}/asset/${assetId}`,
    );
    return unwrapResult<PlannerActivity>(response);
  },

  /* ------------------------------------------------------------------ */
  /* Unplanned work + resource pools                                    */
  /* ------------------------------------------------------------------ */

  listUnplannedWork: async (
    _filters: UnplannedWorkFilters = {},
    signal?: AbortSignal,
  ): Promise<UnplannedWorkItem[]> => {
    try {
      const response = await api.get<unknown>(
        `${BASE}/unplanned-work`,
        undefined,
        signal ? { signal } : undefined,
      );
      return toArray<UnplannedWorkItem>(response);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 204) return [];
      throw err;
    }
  },

  getAssetPool: async (
    filters: AssetPoolFilters,
    signal?: AbortSignal,
  ): Promise<AssetPool> => {
    const response = await api.get<AssetPool>(
      `${BASE}/asset-load`,
      { date: filters.date },
      signal ? { signal } : undefined,
    );
    return (
      response ?? {
        date: filters.date,
        available: [] as AssetPoolItem[],
        assigned: [],
      }
    );
  },

  getCrewPool: async (
    filters: CrewPoolFilters,
    signal?: AbortSignal,
  ): Promise<CrewPool> => {
    const response = await api.get<CrewPool>(
      `${BASE}/crew-load`,
      { date: filters.date },
      signal ? { signal } : undefined,
    );
    return response ?? { date: filters.date, available: [], assigned: [] };
  },

  /* ------------------------------------------------------------------ */
  /* Execution lines                                                    */
  /* ------------------------------------------------------------------ */

  listExecutionLines: async (
    activityId: string,
  ): Promise<PlannerExecutionLine[]> => {
    try {
      const response = await api.get<unknown>(
        `${BASE}/${activityId}/execution-lines`,
      );
      return toArray<PlannerExecutionLine>(response);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 204) return [];
      throw err;
    }
  },

  captureExecution: async (
    activityId: string,
    payload: ExecutionCapturePayload,
  ): Promise<void> => {
    await api.create<void>(`${BASE}/${activityId}/execution-capture`, payload);
  },
};

/* -------------------------------------------------------------------------- */
/* Agent B — append crew-availability / activity-task helpers below this seam */
/* -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- */
/* Agent C — append insights / site-workload helpers below this seam          */
/* -------------------------------------------------------------------------- */
